import {Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {NgForm} from '@angular/forms';
import {RegisterCompanyStepBase} from '../register-company-step.base';
import {
    AddressLookupResult,
    AddressLookupService
} from '../../../../services/address-lookup/address-lookup.service';

@Component({
    selector: 'app-register-company-company-info',
    templateUrl: './company-info-step.component.html',
    styleUrls: ['../../register-company.component.scss']
})
export class RegisterCompanyCompanyInfoStepComponent extends RegisterCompanyStepBase implements OnInit, OnDestroy {

    @Input() provinces: { id: string; name: string }[] = [];
    @Input() cities: any[] = [];

    @Output() companyCitiesNeedUpdate = new EventEmitter<void>();

    addressResults: AddressLookupResult[] = [];
    addressSearchError = '';
    isSearchingAddress = false;
    isAddressDropdownOpen = false;
    highlightedAddressIndex = 0;
    submitted = false;

    private searchDebounce: ReturnType<typeof setTimeout> | null = null;
    private searchRequestId = 0;
    private skipNextAddressSearch = false;
    private cityApplyTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(private addressLookupService: AddressLookupService) {
        super();
    }

    ngOnInit(): void {
        if (this.company.province) {
            this.updateCitiesCompany();
        }
    }

    updateCitiesCompany(): void {
        this.companyCitiesNeedUpdate.emit();
    }

    get fiscalCodeLength(): number {
        return (this.company.fiscalCode || '').replace(/\s+/g, '').length;
    }

    get vatLength(): number {
        return (this.company.vat || '').replace(/\D/g, '').length;
    }

    get ibanLength(): number {
        return (this.company.iban || '').replace(/\s+/g, '').length;
    }

    get formattedIban(): string {
        const iban = (this.company.iban || '').replace(/\s+/g, '').toUpperCase();
        return iban.replace(/(.{4})/g, '$1 ').trim();
    }

    get isIbanValid(): boolean {
        const iban = (this.company.iban || '').replace(/\s+/g, '').toUpperCase();
        return /^IT[0-9A-Z]{25}$/.test(iban);
    }

    normalizeFiscalCode(): void {
        if (typeof this.company.fiscalCode === 'string') {
            this.company.fiscalCode = this.company.fiscalCode.replace(/\s+/g, '').toUpperCase();
        }
        this.persistForm();
    }

    normalizeVat(): void {
        if (typeof this.company.vat === 'string') {
            this.company.vat = this.company.vat.replace(/\D/g, '').slice(0, 11);
        }
        this.persistForm();
    }

    normalizeSdi(): void {
        if (typeof this.company.sdi === 'string') {
            this.company.sdi = this.company.sdi.replace(/\s+/g, '').toUpperCase().slice(0, 7);
        }
        this.persistForm();
    }

    normalizeCompanyEmail(): void {
        if (typeof this.company.companyEmail === 'string') {
            this.company.companyEmail = this.company.companyEmail.trim();
        }
        this.persistForm();
    }

    onIbanInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const digitsBeforeCursor = input.value
            .slice(0, input.selectionStart ?? input.value.length)
            .replace(/\s+/g, '').length;
        this.company.iban = (input.value ?? '').replace(/\s+/g, '').toUpperCase().slice(0, 27);
        const formatted = this.formattedIban;
        input.value = formatted;
        const position = this.cursorFromCharIndex(formatted, digitsBeforeCursor);
        input.setSelectionRange(position, position);
        this.persistForm();
    }

    onAddressChange(value: string): void {
        this.company.address = value;
        this.persistForm();
        this.addressSearchError = '';

        if (this.skipNextAddressSearch) {
            this.skipNextAddressSearch = false;
            return;
        }

        this.clearSearchDebounce();
        this.searchRequestId++;
        const query = (value ?? '').trim();
        if (query.length < 5) {
            this.addressResults = [];
            this.isAddressDropdownOpen = false;
            this.isSearchingAddress = false;
            return;
        }

        this.isSearchingAddress = true;
        this.isAddressDropdownOpen = true;
        this.searchDebounce = setTimeout(() => {
            void this.searchAddress();
        }, 350);
    }

    onAddressFocus(): void {
        if (this.addressResults.length > 0 || this.addressSearchError) {
            this.isAddressDropdownOpen = true;
        }
    }

    onAddressKeydown(event: KeyboardEvent): void {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!this.isAddressDropdownOpen || this.addressResults.length === 0) {
                void this.searchAddress();
                return;
            }
            this.highlightedAddressIndex = Math.min(this.highlightedAddressIndex + 1, this.addressResults.length - 1);
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.highlightedAddressIndex = Math.max(this.highlightedAddressIndex - 1, 0);
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            const selected = this.addressResults[this.highlightedAddressIndex];
            if (selected) {
                this.selectAddress(selected);
            } else {
                void this.searchAddress();
            }
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            this.closeAddressDropdown();
        }
    }

    async searchAddress(): Promise<void> {
        const query = (this.company.address ?? '').trim();
        this.addressSearchError = '';
        this.clearSearchDebounce();

        if (query.length < 5) {
            this.addressResults = [];
            this.isAddressDropdownOpen = false;
            this.isSearchingAddress = false;
            return;
        }

        const requestId = ++this.searchRequestId;
        this.isSearchingAddress = true;
        this.isAddressDropdownOpen = true;

        try {
            const results = await this.addressLookupService.searchItalianAddress(query);
            if (requestId !== this.searchRequestId) return;

            this.addressResults = results;
            this.highlightedAddressIndex = 0;
            this.isAddressDropdownOpen = true;
            if (results.length === 0) {
                this.addressSearchError = 'Nessun indirizzo trovato. Prova ad aggiungere città o CAP.';
            }
        } catch (error) {
            if (requestId !== this.searchRequestId) return;
            console.error('Address lookup failed:', error);
            this.addressResults = [];
            this.addressSearchError = 'La ricerca indirizzo non è disponibile. Puoi compilare i campi manualmente.';
        } finally {
            if (requestId === this.searchRequestId) {
                this.isSearchingAddress = false;
            }
        }
    }

    selectAddress(result: AddressLookupResult): void {
        const address = result.address ?? {};
        const street = address.road ?? address.pedestrian ?? address.footway;
        const city = address.city ?? address.town ?? address.village ?? address.municipality;

        this.skipNextAddressSearch = true;
        this.company.address = street || result.display_name.split(',')[0];
        if (address.house_number) this.company.addressNumber = address.house_number as any;
        if (address.postcode) this.company.zip = address.postcode;

        const provinceCode = this.resolveProvinceCode(address);
        if (provinceCode) {
            this.company.province = provinceCode;
            this.updateCitiesCompany();
        }
        this.scheduleCityApply(city);

        this.addressResults = [];
        this.addressSearchError = '';
        this.closeAddressDropdown();
        this.persistForm();
    }

    getAddressTitle(result: AddressLookupResult): string {
        const address = result.address ?? {};
        const street = [address.road ?? address.pedestrian ?? address.footway, address.house_number]
            .filter(Boolean)
            .join(' ');
        return street || result.display_name.split(',')[0];
    }

    getAddressSubtitle(result: AddressLookupResult): string {
        const address = result.address ?? {};
        const city = address.city ?? address.town ?? address.village ?? address.municipality;
        return [city, address.postcode].filter(Boolean).join(' · ');
    }

    tryContinue(form: NgForm): void {
        this.submitted = true;
        form.control.markAllAsTouched();
        if (form.invalid || !this.isIbanValid) return;
        this.nextStep();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        if (!target?.closest('[data-address-autocomplete]')) {
            this.closeAddressDropdown();
        }
    }

    ngOnDestroy(): void {
        this.clearSearchDebounce();
        if (this.cityApplyTimeout) {
            clearTimeout(this.cityApplyTimeout);
            this.cityApplyTimeout = null;
        }
    }

    private closeAddressDropdown(): void {
        this.isAddressDropdownOpen = false;
        this.highlightedAddressIndex = 0;
    }

    private clearSearchDebounce(): void {
        if (this.searchDebounce) {
            clearTimeout(this.searchDebounce);
            this.searchDebounce = null;
        }
    }

    private scheduleCityApply(city?: string): void {
        if (!city) return;
        if (this.cityApplyTimeout) clearTimeout(this.cityApplyTimeout);
        this.cityApplyTimeout = setTimeout(() => {
            this.company.city = this.matchCity(city);
            this.persistForm();
            this.cityApplyTimeout = null;
        });
    }

    private matchCity(city: string): string {
        const normalized = this.normalizeProvinceName(city);
        return this.cities.find(option => this.normalizeProvinceName(String(option)) === normalized) ?? city;
    }

    private resolveProvinceCode(address: Record<string, string | undefined>): string | undefined {
        const isoProvince = Object.entries(address)
            .find(([key, value]) => key.startsWith('ISO3166-2-lvl') && /^IT-[A-Z]{2}$/i.test(value ?? ''))?.[1]
            ?.split('-')[1]
            ?.toUpperCase();
        if (isoProvince && this.provinces.some(province => province.id === isoProvince)) return isoProvince;

        const candidates = [address['county'], address['state_district']]
            .filter((value): value is string => !!value)
            .map(value => this.normalizeProvinceName(value));
        return this.provinces.find(province => candidates.includes(this.normalizeProvinceName(province.name)))?.id;
    }

    private normalizeProvinceName(value: string): string {
        return value
            .toLocaleLowerCase('it')
            .replace(/provincia (di|del|della) /g, '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();
    }

    private cursorFromCharIndex(formatted: string, charIndex: number): number {
        if (charIndex <= 0) return 0;
        let seen = 0;
        for (let i = 0; i < formatted.length; i++) {
            if (formatted[i] !== ' ') {
                seen++;
                if (seen === charIndex) return i + 1;
            }
        }
        return formatted.length;
    }
}
